// src/redux/slices/blogdetailsSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const BASE_URL = 'https://evercorenutrition.com/ev_admin/api';

// Async thunk for fetching blog with comments
export const fetchBlogWithComments = createAsyncThunk(
  'blogdetails/fetchBlogWithComments',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/blogs/${id}/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async thunk for posting a comment
export const postComment = createAsyncThunk(
  'blogdetails/postComment',
  async ({ blogId, name, email, comment }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/blogs/${blogId}/comments/`, {
        name,
        email,
        comment,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async thunk for fetching related blogs (by tags)
export const fetchRelatedBlogs = createAsyncThunk(
  'blogdetails/fetchRelatedBlogs',
  async (tags, { rejectWithValue }) => {
    try {
      // If tags exist, fetch blogs with similar tags
      if (tags) {
        const tagArray = tags.split(',').map(tag => tag.trim());
        // Get first tag for related blogs
        const response = await axios.get(`${BASE_URL}/blogs/?tag=${encodeURIComponent(tagArray[0])}`);
        return response.data;
      }
      return { results: [] };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  blog: null,
  comments: [],
  relatedBlogs: [],
  status: 'idle',
  commentStatus: 'idle',
  relatedStatus: 'idle',
  error: null,
};

const blogdetailsSlice = createSlice({
  name: 'blogdetails',
  initialState,
  reducers: {
    clearBlogDetails: (state) => {
      state.blog = null;
      state.comments = [];
      state.relatedBlogs = [];
      state.status = 'idle';
      state.error = null;
    },
    addComment: (state, action) => {
      state.comments.unshift(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch blog with comments
      .addCase(fetchBlogWithComments.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchBlogWithComments.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.blog = action.payload;
        state.comments = action.payload.comments || [];
      })
      .addCase(fetchBlogWithComments.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch blog';
      })

      // Post comment
      .addCase(postComment.pending, (state) => {
        state.commentStatus = 'loading';
      })
      .addCase(postComment.fulfilled, (state, action) => {
        state.commentStatus = 'succeeded';
        state.comments.unshift(action.payload);
      })
      .addCase(postComment.rejected, (state, action) => {
        state.commentStatus = 'failed';
        state.error = action.payload || 'Failed to post comment';
      })

      // Fetch related blogs
      .addCase(fetchRelatedBlogs.pending, (state) => {
        state.relatedStatus = 'loading';
      })
      .addCase(fetchRelatedBlogs.fulfilled, (state, action) => {
        state.relatedStatus = 'succeeded';
        state.relatedBlogs = action.payload.results || [];
      })
      .addCase(fetchRelatedBlogs.rejected, (state, action) => {
        state.relatedStatus = 'failed';
      });
  },
});

export const { clearBlogDetails, addComment } = blogdetailsSlice.actions;

// Selectors
export const selectBlog = (state) => state.blogdetails.blog;
export const selectBlogComments = (state) => state.blogdetails.comments;
export const selectRelatedBlogs = (state) => state.blogdetails.relatedBlogs;
export const selectBlogDetailsStatus = (state) => state.blogdetails.status;
export const selectCommentStatus = (state) => state.blogdetails.commentStatus;
export const selectRelatedStatus = (state) => state.blogdetails.relatedStatus;
export const selectBlogDetailsError = (state) => state.blogdetails.error;

export default blogdetailsSlice.reducer;